import { useState, useEffect } from 'react';
import { toast } from '../components/UI';
import BrandLogo from '../components/BrandLogo';
import { Building2, Hospital, Check, Stethoscope } from 'lucide-react';

const API_BASE = '/api';

// Brand gradient — THMallorca teal, intentionally theme-agnostic
const BRAND_GRADIENT = 'linear-gradient(135deg, #2F6BEA, #1E46B4)';
const BRAND_PRIMARY = '#2557D6';

export default function AccessPage({ slug, onGoToLogin }) {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null); // 'client' | 'professional'
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', password: '', password2: '',
    // Client fields
    company_name: '', cif: '',
    // Pro fields
    specialization: 'TES', license_number: '', dni: '',
  });

  useEffect(() => {
    fetch(`${API_BASE}/auth/tenant-info/${slug}`)
      .then(r => r.ok ? r.json() : Promise.reject('Not found'))
      .then(setTenant)
      .catch(() => setError('Organización no encontrada'))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleSubmit = async (type) => {
    if (!form.first_name || !form.last_name || !form.email || !form.password) {
      toast.warning('Faltan campos', 'Rellena todos los campos obligatorios');
      return;
    }
    if (form.password !== form.password2) {
      toast.warning('Revisa la contraseña', 'Las contraseñas no coinciden');
      return;
    }
    if (form.password.length < 8) {
      toast.warning('Contraseña insegura', 'La contraseña debe tener al menos 8 caracteres');
      return;
    }

    setSubmitting(true);
    try {
      const endpoint = type === 'client' ? '/auth/register-client' : '/auth/register-professional';
      const body = {
        ...form,
        tenant_slug: slug,
      };
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error en el registro');
      setSuccess(data.message);
    } catch (err) {
      toast.error('Error', err.message);
    }
    setSubmitting(false);
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Cargando...</p>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', fontFamily: "'DM Sans', sans-serif", padding: 20 }}>
      <div style={{ textAlign: 'center', maxWidth: 480, padding: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16, color: 'var(--text-dim)' }}><Hospital size={44} /></div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: '0 0 8px' }}>Organización no encontrada</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: '0 0 28px', lineHeight: 1.5 }}>
          El enlace de acceso <code style={{ background: 'var(--surface-alt)', padding: '2px 6px', borderRadius: 4, fontSize: 13 }}>/access/{slug}</code> no corresponde a ninguna empresa activa.
        </p>

        {/* CTA principal: si eres una empresa que necesita servicios sanitarios → registra TU empresa.
            Esto cubre el caso "el cliente abre la URL pero la empresa que se la mandó no existe / está suspendida". */}
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 14, textAlign: 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: BRAND_GRADIENT, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}><Building2 size={18} /></div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>¿Eres una empresa de ambulancias?</div>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, margin: '0 0 12px' }}>
            Crea tu cuenta de MedCover Pro para gestionar coberturas sanitarias, profesionales, vehículos y centralita de despacho.
          </p>
          <button onClick={() => { window.location.href = '/?view=signup_tenant'; }} style={{
            width: '100%', padding: '12px 20px', background: BRAND_GRADIENT, color: '#fff',
            border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer',
          }}>Registrar mi empresa →</button>
        </div>

        <button onClick={onGoToLogin} style={{
          padding: '10px 24px', background: 'transparent', color: 'var(--text-muted)',
          border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>¿Ya tienes cuenta? Ir al login</button>
      </div>
    </div>
  );

  if (success) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ textAlign: 'center', maxWidth: 440, padding: 32 }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'color-mix(in srgb, var(--success) 18%, transparent)', color: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}><Check size={30} /></div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: '0 0 8px' }}>¡Registro completado!</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: '0 0 24px', lineHeight: 1.6 }}>{success}</p>
        <button onClick={onGoToLogin} style={{ padding: '12px 28px', background: 'var(--accent)', color: 'var(--accent-on)', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>Acceder al portal</button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif", padding: 20 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;900&display=swap" rel="stylesheet" />

      <div style={{ maxWidth: 480, width: '100%' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>{tenant.logo_url ? <img src={tenant.logo_url} alt="" style={{ height: 48, maxWidth: 160, objectFit: 'contain' }} /> : <BrandLogo size={44} showText={false} />}</div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>{tenant.name}</h1>
          <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: 0 }}>Portal de acceso · MedCover Pro</p>
        </div>

        {/* Mode selection */}
        {!mode && (
          <div style={{ display: 'grid', gap: 12 }}>
            {tenant.allow_client_registration && (
              <button onClick={() => setMode('client')} style={{ padding: 24, background: 'var(--card)', border: '2px solid var(--border)', borderRadius: 16, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}
                onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: 'color-mix(in srgb, var(--accent) 12%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)' }}><Building2 size={24} /></div>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>Soy Cliente</div>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Quiero solicitar coberturas sanitarias para mis eventos</div>
                  </div>
                </div>
              </button>
            )}
            <button onClick={() => setMode('professional')} style={{ padding: 24, background: 'var(--card)', border: '2px solid var(--border)', borderRadius: 16, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}
              onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: 'color-mix(in srgb, var(--blue) 14%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--blue)' }}><Stethoscope size={24} /></div>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>Soy Profesional Sanitario</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Quiero darme de alta como profesional de esta empresa</div>
                </div>
              </div>
            </button>
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button onClick={onGoToLogin} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>¿Ya tienes cuenta? Inicia sesión</button>
            </div>
          </div>
        )}

        {/* Registration form */}
        {mode && (
          <div style={{ background: 'var(--card)', borderRadius: 16, padding: 28, border: '1px solid var(--border)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
            <button onClick={() => setMode(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer', marginBottom: 12, padding: 0 }}>← Volver</button>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>
              {mode === 'client' ? 'Registro como Cliente' : 'Registro como Profesional'}
            </h2>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 20px' }}>
              {mode === 'client' ? 'Crea tu cuenta para solicitar coberturas sanitarias' : 'Tu solicitud será revisada por el administrador'}
            </p>

            {/* Helper para etiqueta con asterisco rojo si required */}
            {/* Aplicado inline porque AccessPage no usa FormField (estilos brand-tealos
               específicos del portal público). */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Nombre <span style={{ color: '#EF4444' }}>*</span></label>
                <input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} style={inputStyle} placeholder="Juan" maxLength={50} autoComplete="given-name" required />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Apellidos <span style={{ color: '#EF4444' }}>*</span></label>
                <input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} style={inputStyle} placeholder="García" maxLength={80} autoComplete="family-name" required />
              </div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Email <span style={{ color: '#EF4444' }}>*</span></label>
              <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} style={inputStyle} placeholder="tu@email.com" maxLength={254} autoComplete="email" required />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Teléfono</label>
              <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} style={inputStyle} placeholder="+34 600 000 000" maxLength={20} autoComplete="tel" />
            </div>

            {/* Client specific fields */}
            {mode === 'client' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Empresa <span style={{ color: '#EF4444' }}>*</span></label>
                  <input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} style={inputStyle} placeholder="Mi Empresa SL" maxLength={120} autoComplete="organization" required />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>CIF</label>
                  <input value={form.cif} onChange={e => setForm({...form, cif: e.target.value.toUpperCase()})} style={inputStyle} placeholder="B12345678" maxLength={9} />
                </div>
              </div>
            )}

            {/* Professional specific fields */}
            {mode === 'professional' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Categoría <span style={{ color: '#EF4444' }}>*</span></label>
                    <select value={form.specialization} onChange={e => setForm({...form, specialization: e.target.value})} style={inputStyle} required>
                      <option value="TES">TES</option>
                      <option value="enfermero">Enfermero/a</option>
                      <option value="medico_sva">Médico/a</option>
                      <option value="supervisor">Supervisor/a</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>DNI / NIE <span style={{ color: '#EF4444' }}>*</span></label>
                    <input value={form.dni} onChange={e => setForm({...form, dni: e.target.value.toUpperCase()})} style={inputStyle} placeholder="12345678A" maxLength={9} pattern="[0-9XYZ][0-9]{7}[A-Z]" required />
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Nº Colegiado / Licencia</label>
                  <input value={form.license_number} onChange={e => setForm({...form, license_number: e.target.value})} style={inputStyle} placeholder="280012345" maxLength={20} />
                </div>
              </>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Contraseña <span style={{ color: '#EF4444' }}>*</span></label>
                <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} style={inputStyle} placeholder="Mín. 8 caracteres" minLength={8} maxLength={128} autoComplete="new-password" required />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Repetir <span style={{ color: '#EF4444' }}>*</span></label>
                <input type="password" value={form.password2} onChange={e => setForm({...form, password2: e.target.value})} style={{ ...inputStyle, border: form.password2 && form.password !== form.password2 ? '2px solid #EF4444' : inputStyle.border }} placeholder="Repetir contraseña" minLength={8} maxLength={128} autoComplete="new-password" required />
                {form.password2 && form.password !== form.password2 && (
                  <div style={{ color: '#EF4444', fontSize: 11, marginTop: 3, fontWeight: 600 }}>No coincide con la contraseña</div>
                )}
              </div>
            </div>

            <button onClick={() => handleSubmit(mode)} disabled={submitting} style={{ width: '100%', padding: '12px 0', background: 'var(--accent)', color: 'var(--accent-on)', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
              {submitting ? 'Registrando...' : mode === 'client' ? 'Crear cuenta de cliente' : 'Enviar solicitud de registro'}
            </button>

            {mode === 'professional' && (
              <p style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 10, textAlign: 'center' }}>Tu registro será revisado por el administrador antes de activar tu cuenta</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface-alt)',
  color: 'var(--text)',
  fontSize: 14,
  boxSizing: 'border-box',
  outline: 'none',
  transition: 'border-color 0.15s',
};
